/**
 * ТЗ 0106, этап 3 — доска «Воронка» раздела «Лиды»: чистые помощники.
 *
 * Колонка — этап воронки New leads (порядок `sort`), каждая тянет СВОЮ
 * страницу `kommoLeads(statusIds: [id])` под теми же фильтрами, что таблица;
 * шапки — `kommoLeadStageCounts` под теми же фильтрами (кроме этапа).
 * Закрытые этапы (142 «успешно», 143 «закрыто и не реализовано») — только при
 * «Показать закрытые»: по умолчанию воронка — про живых лидов.
 *
 * Модуль без React, Apollo и i18n — всё здесь проверяется юнит-тестами.
 */

import moment from 'moment';

import { APLORA_GOOD_WIN_CHANCE, APLORA_HIGH_WIN_CHANCE } from './aploraModel';

import {
  ILeadsFilters,
  LEAD_CREATED_RECENT,
  LEAD_RESPONSIBLE_NONE,
  leadsCreatedFrom,
  toLeadsQueryVariables,
} from './leadsModel';

/** Системные этапы Kommo: успешно реализовано и закрыто и не реализовано. */
export const LEAD_STAGE_WON = '142';
export const LEAD_STAGE_LOST = '143';

/**
 * Страница колонки: первая и каждая дозагрузка (ТЗ 0128 — без потолка:
 * страницы дописываются `fetchMore`, а не перечитываются растущим `limit`).
 */
export const LEAD_BOARD_PAGE = 30;

/**
 * Вид раздела «Лиды» в адресе. ТЗ 0120: по умолчанию — «Воронка» (доска),
 * в адрес пишется только `?view=table`; старый `?view=board` понимается.
 */
export const LEADS_VIEW_PARAM = 'view';
export const LEADS_VIEWS = ['board', 'table'] as const;
export type LeadsView = typeof LEADS_VIEWS[number];

/** ТЗ 0120: последний выбранный вид — в localStorage этого браузера. */
export const LEADS_VIEW_STORAGE_KEY = 'erp.leads.view';

const isLeadsView = (value: unknown): value is LeadsView =>
  value === 'board' || value === 'table';

/** Запомненный вид; хранилище недоступно (приватное окно) — `null`. */
export const readStoredLeadsView = (): LeadsView | null => {
  try {
    const value = window.localStorage.getItem(LEADS_VIEW_STORAGE_KEY);
    return isLeadsView(value) ? value : null;
  } catch (error) {
    return null;
  }
};

export const storeLeadsView = (view: LeadsView): void => {
  try {
    window.localStorage.setItem(LEADS_VIEW_STORAGE_KEY, view);
  } catch (error) {
    // Хранилище закрыто — вид просто не запомнится.
  }
};

/** Вид: адрес главнее, затем запомненный, по умолчанию — доска. */
export const parseLeadsView = (
  search: string,
  stored: LeadsView | null = null,
): LeadsView => {
  const value = new URLSearchParams(search).get(LEADS_VIEW_PARAM);
  if (isLeadsView(value)) return value;
  return stored ?? 'board';
};

/** Адрес с видом: доска — без параметра (адрес по умолчанию не растёт). */
export const withLeadsView = (search: string, view: LeadsView): string => {
  const params = new URLSearchParams(search);
  if (view === 'table') params.set(LEADS_VIEW_PARAM, 'table');
  else params.delete(LEADS_VIEW_PARAM);
  // Страница таблицы доске не нужна, а при возврате — первая страница.
  params.delete('page');
  return params.toString();
};

export interface ILeadBoardStage {
  id: string;
  name: string;
  color: string;
  sort: number;
  isClosed: boolean;
}

/** Закрытый этап (142/143 или помеченный закрытым у источника). */
export const isLeadClosedStage = (stage: {
  id: string;
  isClosed?: boolean | null;
}): boolean =>
  Boolean(stage.isClosed) ||
  stage.id === LEAD_STAGE_WON ||
  stage.id === LEAD_STAGE_LOST;

/**
 * Колонки доски: этапы по `sort`, закрытые — только при «Показать закрытые»;
 * выбранные в фильтре этапы — только они (как у доски дофинансирования:
 * выбор этапа = «покажи только его»).
 */
export const leadBoardStages = <T extends ILeadBoardStage>(
  stages: ReadonlyArray<T>,
  filters: Pick<ILeadsFilters, 'includeClosed' | 'stages'> &
    Partial<Pick<ILeadsFilters, 'chance'>>,
): T[] => {
  // ТЗ 0120: фильтр шанса — только про открытых лидов: с ним закрытых
  // колонок нет и при «Показать закрытые» (ТЗ 0127: и «хороший и выше»).
  const withClosed = filters.includeClosed && !filters.chance;
  const ordered = [...stages]
    .filter((stage) => withClosed || !isLeadClosedStage(stage))
    .sort((a, b) => a.sort - b.sort || Number(a.id) - Number(b.id));
  if (!filters.stages.length) return ordered;
  const picked = ordered.filter((stage) => filters.stages.includes(stage.id));
  return picked.length ? picked : ordered;
};

/** Переменные колонки: фильтры таблицы, ОДИН этап, первая страница. */
export const toLeadBoardColumnVariables = (
  filters: ILeadsFilters,
  stage: Pick<ILeadBoardStage, 'id' | 'isClosed'>,
  limit: number,
) => ({
  ...toLeadsQueryVariables({ ...filters, page: 0 }, limit),
  statusIds: [stage.id],
  // Закрытый этап сервер отдаёт только с `includeClosed`.
  includeClosed:
    isLeadClosedStage(stage) || filters.includeClosed ? true : null,
  offset: 0,
  limit,
});

/** Переменные счётчиков по этапам: те же фильтры без этапа и страницы. */
export const toLeadStageCountVariables = (
  filters: ILeadsFilters,
  today?: string,
) => {
  const variables = toLeadsQueryVariables(filters, undefined, today);
  return {
    responsible: variables.responsible,
    search: variables.search,
    includeClosed: variables.includeClosed,
    ladder: variables.ladder,
    utmSource: variables.utmSource,
    utmMedium: variables.utmMedium,
    utmCampaign: variables.utmCampaign,
    fundingStatus: variables.fundingStatus,
    hasFunding: variables.hasFunding,
    hasStudent: variables.hasStudent,
    createdFrom: variables.createdFrom,
    aploraMinWinChance: variables.aploraMinWinChance,
    nextContact: variables.nextContact,
    aploraCategories: variables.aploraCategories,
    // ТЗ 0120: 🔥 в шапке колонки — лиды с шансом не ниже порога.
    hotMinWinChance: APLORA_HIGH_WIN_CHANCE,
    // ТЗ 0127: «хороших» — от 0,55 и ниже горячих.
    goodMinWinChance: APLORA_GOOD_WIN_CHANCE,
  };
};

/**
 * Метрика «новых за 7 дн.» (ТЗ 0120 — мини-метрика шапки вместо плитки):
 * те же фильтры, что список (этапы и фильтры Aplora — тоже), но окно
 * создания — всегда последние 7 дней по Варшаве, даже когда фильтр «за 7
 * дней» снят: метрика показывает, сколько лидов он оставит.
 */
export const toRecentLeadsVariables = (
  filters: ILeadsFilters,
  today?: string,
) => {
  const variables = toLeadsQueryVariables(filters, undefined, today);
  return {
    statusIds: variables.statusIds,
    responsible: variables.responsible,
    search: variables.search,
    includeClosed: variables.includeClosed,
    ladder: variables.ladder,
    utmSource: variables.utmSource,
    utmMedium: variables.utmMedium,
    utmCampaign: variables.utmCampaign,
    fundingStatus: variables.fundingStatus,
    hasFunding: variables.hasFunding,
    hasStudent: variables.hasStudent,
    aploraMinWinChance: variables.aploraMinWinChance,
    nextContact: variables.nextContact,
    aploraCategories: variables.aploraCategories,
    createdFrom: leadsCreatedFrom(LEAD_CREATED_RECENT, today) as string,
  };
};

/** Плитка-фильтр «Новые за 7 дней»: включает окно или снимает его. */
export const toggleRecentLeads = (
  filters: Pick<ILeadsFilters, 'created'>,
): Pick<ILeadsFilters, 'created'> => ({
  created: filters.created ? '' : LEAD_CREATED_RECENT,
});

/** Счётчики «без ответственного» — те же фильтры, ответственный `none`. */
export const toUnassignedCountVariables = (filters: ILeadsFilters) => ({
  ...toLeadStageCountVariables(filters),
  responsible: LEAD_RESPONSIBLE_NONE,
});

export interface ILeadStageCount {
  statusId: string;
  count: number;
  priceSum: number;
  /** ТЗ 0120: лидов с шансом Aplora ≥ порога (`hotMinWinChance`). */
  hotCount?: number | null;
  /** ТЗ 0127: «хороших» — ≥ `goodMinWinChance` и ниже горячих. */
  goodCount?: number | null;
}

export const leadStageCountMap = (
  rows: ReadonlyArray<ILeadStageCount>,
): ReadonlyMap<string, ILeadStageCount> =>
  new Map(rows.map((row) => [String(row.statusId), row]));

/**
 * ТЗ 0121 — сколько лидов показывает доска: сумма счётчиков её колонок
 * (`leadBoardStages`). Лид без этапа (`stage = null`, Kommo не прислал
 * статус) в колонку не попадает — его видно только в «Таблице»; «Показать N»
 * листа фильтров на доске называет это же число, а не итог таблицы.
 */
export const leadBoardTotal = (
  stages: ReadonlyArray<ILeadBoardStage>,
  filters: Parameters<typeof leadBoardStages>[1],
  counts: ReadonlyArray<ILeadStageCount>,
): number => {
  const byId = leadStageCountMap(counts);
  return leadBoardStages(stages, filters).reduce(
    (sum, stage) => sum + (byId.get(String(stage.id))?.count ?? 0),
    0,
  );
};

/**
 * Плитки-итоги над «Лидами»: в работе, без ответственного, сумма (и «успешно»
 * при закрытых). «Новые за 7 дней» — своим запросом (`toRecentLeadsVariables`).
 */
export interface ILeadsSummary {
  open: number;
  /** ТЗ 0120: открытых лидов с высоким шансом Aplora. */
  hot: number;
  /** ТЗ 0127: открытых с «хорошим» шансом (без высоких). */
  good: number;
  unassigned: number;
  openSum: number;
  won: number | null;
}

export const leadsSummary = (
  stages: ReadonlyArray<ILeadBoardStage>,
  counts: ReadonlyArray<ILeadStageCount>,
  unassigned: ReadonlyArray<ILeadStageCount>,
): ILeadsSummary => {
  const byId = leadStageCountMap(counts);
  const openIds = new Set(
    stages
      .filter((stage) => !isLeadClosedStage(stage))
      .map((stage) => stage.id),
  );
  const sum = (
    rows: ReadonlyArray<ILeadStageCount>,
    key: 'count' | 'priceSum' | 'hotCount' | 'goodCount',
  ) =>
    rows
      .filter((row) => openIds.has(String(row.statusId)))
      .reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
  const won = byId.get(LEAD_STAGE_WON);
  return {
    open: sum(counts, 'count'),
    hot: sum(counts, 'hotCount'),
    good: sum(counts, 'goodCount'),
    unassigned: sum(unassigned, 'count'),
    openSum: sum(counts, 'priceSum'),
    won: won ? won.count : null,
  };
};

/** Целых дней на этапе; нет даты — `null`. */
export const leadDaysOnStage = (
  enteredAt: string | null | undefined,
  now: moment.Moment = moment(),
): number | null => {
  if (!enteredAt) return null;
  const at = moment(enteredAt);
  if (!at.isValid()) return null;
  return Math.max(0, now.diff(at, 'days'));
};

/** Инициалы для аватара: «Piotr Sprzedawca» → «PS», одно слово — две буквы. */
export const personInitials = (name: string | null | undefined): string => {
  const words = (name || '')
    .replace(/\(.*?\)/g, ' ')
    .split(/\s+/)
    .map((word) => word.replace(/^[^0-9A-Za-zÀ-ɏЀ-ӿ]+/, ''))
    .filter(Boolean);
  if (!words.length) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Что спросить перед переносом: в 143 Kommo требует причину отказа, в 142 —
 * подтверждение (закрытие сделки); в открытый этап — ничего.
 */
export type LeadMoveAsk = 'lossReason' | 'confirmWon' | null;

export const leadMoveAsk = (statusId: string): LeadMoveAsk => {
  if (statusId === LEAD_STAGE_LOST) return 'lossReason';
  if (statusId === LEAD_STAGE_WON) return 'confirmWon';
  return null;
};

/**
 * Оптимистичный перенос: пока Kommo отвечает, карточка уже в новой колонке.
 * `pending` — `id лида → { лид, откуда, куда }`.
 */
export interface ILeadBoardMove<T> {
  lead: T;
  from: string;
  to: string;
}

/**
 * Строки колонки с учётом переносов в пути: уехавшие — прочь, приехавшие —
 * сверху (если колонка их ещё не знает). Кроме того, строка показывается,
 * только если её этап в кэше — этот (ответ переноса уже записал новый этап,
 * а перечитывание колонки ещё не пришло).
 */
export const leadBoardColumnRows = <
  T extends { id: string; stage?: { id: string } | null }
>(
  stageId: string,
  rows: ReadonlyArray<T>,
  pending: ReadonlyMap<string, ILeadBoardMove<T>>,
): T[] => {
  const kept = rows.filter((row) => {
    const move = pending.get(row.id);
    if (move) return move.to === stageId;
    return !row.stage || String(row.stage.id) === stageId;
  });
  const known = new Set(kept.map((row) => row.id));
  const incoming = Array.from(pending.values())
    .filter((move) => move.to === stageId && !known.has(move.lead.id))
    .map((move) => move.lead);
  return [...incoming, ...kept];
};

/**
 * Перенос закончен (Kommo ответил и колонки перечитаны): запись уходит, но
 * только если это всё ещё ЭТОТ перенос — второй перенос того же лида мог
 * заменить её за время ответа.
 */
export const settleLeadBoardMove = <T>(
  moves: ReadonlyMap<string, ILeadBoardMove<T>>,
  id: string,
  entry: ILeadBoardMove<T>,
): ReadonlyMap<string, ILeadBoardMove<T>> => {
  if (moves.get(id) !== entry) return moves;
  const next = new Map(moves);
  next.delete(id);
  return next;
};

/**
 * Kommo отказал: вернуть то, что было до этого переноса — прежнюю запись
 * (прошлый перенос лида ещё не перечитан колонкой) или ничего.
 */
export const revertLeadBoardMove = <T>(
  moves: ReadonlyMap<string, ILeadBoardMove<T>>,
  id: string,
  entry: ILeadBoardMove<T>,
  previous: ILeadBoardMove<T> | undefined,
): ReadonlyMap<string, ILeadBoardMove<T>> => {
  if (moves.get(id) !== entry) return moves;
  const next = new Map(moves);
  if (previous) next.set(id, previous);
  else next.delete(id);
  return next;
};

/**
 * ТЗ 0128 — переносы, которые касаются колонки (уехал из неё или в неё).
 * Колонке — только её срез: перенос между двумя колонками не перерисовывает
 * остальные. Срез прежний (`previous`), если его записи не изменились, —
 * ссылка та же, и `memo` колонки срабатывает.
 */
export const leadBoardMovesFor = <T>(
  stageId: string,
  moves: ReadonlyMap<string, ILeadBoardMove<T>>,
  previous?: ReadonlyMap<string, ILeadBoardMove<T>>,
): ReadonlyMap<string, ILeadBoardMove<T>> => {
  const slice = new Map<string, ILeadBoardMove<T>>();
  moves.forEach((move, id) => {
    if (move.from === stageId || move.to === stageId) slice.set(id, move);
  });
  if (previous && previous.size === slice.size) {
    let same = true;
    slice.forEach((move, id) => {
      if (previous.get(id) !== move) same = false;
    });
    if (same) return previous;
  }
  return slice;
};

/** Сколько ждать перечитывания колонок после переноса, прежде чем отпустить карточку. */
export const LEAD_BOARD_SETTLE_MS = 15000;

/** Счётчик колонки с учётом переносов в пути. */
export const leadBoardColumnCount = <T>(
  stageId: string,
  serverCount: number,
  pending: ReadonlyMap<string, ILeadBoardMove<T>>,
): number => {
  let count = serverCount;
  pending.forEach((move) => {
    if (move.from === stageId && move.to !== stageId) count -= 1;
    if (move.to === stageId && move.from !== stageId) count += 1;
  });
  return Math.max(0, count);
};

/** Ошибки переноса и смены ответственного (`customErrors.code`). */
export const LEAD_MOVE_ERROR_CODES = [
  'AUTH',
  'NETWORK',
  'RATE_LIMITED',
  'TIMEOUT',
  'ERROR',
  'DISABLED',
  'LEAD_NOT_FOUND',
  'LEAD_DELETED',
  'STAGE_NOT_FOUND',
  'STAGE_OTHER_PIPELINE',
  'LOSS_REASON_NOT_FOUND',
  'SOURCE_MISMATCH',
  'USER_NOT_FOUND',
] as const;
